import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AppSnackbar from "layouts/notifications/Snackbar";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import getLangauges from "services/languageService";
import { updateUser } from "services/userService";
import { useTranslation } from "hooks/useTranslation";
import { normalizeSenegalPhoneInput, validateSenegalPhoneNineDigits } from "utils/senegalPhone";
import {
  translateApiMessage,
  isEmailAlreadyExistsError,
  isPhoneInvalidFormatError,
} from "utils/translateApiMessage";
import useHierarchySelects from "hooks/useHierarchySelects";
import HierarchySelects from "components/HierarchySelects";

// Helper validation function with translation support
function validateForm(form, t) {
  const errors = {};
  if (!form.first_name)
    errors.first_name = t
      ? t("admin.annotators.modals.validation.firstNameRequired", "First Name is required")
      : "First Name is required";
  if (!form.last_name)
    errors.last_name = t
      ? t("admin.annotators.modals.validation.lastNameRequired", "Last Name is required")
      : "Last Name is required";

  if (!form.phoneNumber) {
    errors.phoneNumber = t
      ? t("admin.annotators.modals.validation.phoneRequired", "Phone number is required")
      : "Phone number is required";
  } else {
    const pe = validateSenegalPhoneNineDigits(form.phoneNumber, t);
    if (pe) errors.phoneNumber = pe;
  }

  // Email Validation
  if (!form.email_id) {
    errors.email_id = t
      ? t("admin.annotators.modals.validation.emailRequired", "Email is required")
      : "Email is required";
  } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email_id)) {
    errors.email_id = t
      ? t("admin.annotators.modals.validation.emailInvalid", "Email is invalid")
      : "Email is invalid";
  }

  if (!form.district)
    errors.district = t
      ? t("admin.annotators.modals.validation.iefRequired", "IEF is required")
      : "IEF is required";
  if (!form.language)
    errors.language = t
      ? t("admin.annotators.modals.validation.languageRequired", "Language is required")
      : "Language is required";

  return errors;
}

export default function EditUser({ open, onClose, user, onSuccess, title, successMessageKey }) {
  const { t } = useTranslation();
  const [editingUserId, setEditingUserId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phoneNumber: "",
    email_id: "",
    state: "",
    district: "",
    language: "",
  });
  const [openDropDown, setOpenDropdown] = useState({});
  const [errors, setErrors] = useState({});
  const [languages, setLanguages] = useState([]);

  const hierarchy = useHierarchySelects({
    open: open && Boolean(user),
    initialDepartmentName: user?.state ?? "",
    initialIefName: user?.district ?? "",
  });

  // Toast-snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "",
    message: "",
  });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
      type: "",
      message: "",
    }));
  };

  // Load languages when modal opens
  useEffect(() => {
    const fetchData = async () => {
      try {
        const langRows = await getLangauges();
        setLanguages(Array.isArray(langRows) ? langRows : []);
      } catch (error) {
        console.error("Failed to fetch dropdown & user data", error);
        showSnackbar(
          "error",
          t
            ? t("admin.annotators.modals.errors.loadDropdowns", "Failed to load dropdown data")
            : "Failed to load dropdown data"
        );
      }
    };

    if (open && user) {
      setEditingUserId(user.id);
      setForm({
        first_name: user.first_name,
        last_name: user.last_name,
        phoneNumber: normalizeSenegalPhoneInput(user.phone_number) || user.phone_number || "",
        email_id: user.email_id,
        state: user.state ?? "",
        district: user.district ?? "",
        language: user.language,
      });
      fetchData();
      setErrors({});
    } else if (!open) {
      setEditingUserId(null);
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when `open` or user id changes
  }, [open, user?.id]);

  useEffect(() => {
    if (hierarchy.departmentName || hierarchy.iefName) {
      setForm((prev) => ({
        ...prev,
        state: hierarchy.departmentName,
        district: hierarchy.iefName,
      }));
    }
  }, [hierarchy.departmentName, hierarchy.iefName]);

  /* handlers for form fields on change */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleHierarchyIefChange = (iefId) => {
    hierarchy.handleIefChange(iefId);
    if (errors.district) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.district;
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!editingUserId) {
      showSnackbar(
        "error",
        t("admin.annotators.modals.errors.updateAnnotator", "Failed to update annotator")
      );
      return;
    }

    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    try {
      const successMessage = successMessageKey
        ? t(successMessageKey, "User updated successfully")
        : t("admin.annotators.modals.success.annotatorUpdated", "Annotator updated successfully");
      const ok = await updateUserDetails(form, successMessage);
      if (ok) onSuccess?.(successMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: "",
      last_name: "",
      phoneNumber: "",
      email_id: "",
      state: "",
      district: "",
      language: "",
    });
    setErrors({});
    onClose();
  };

  const handleNumericChange = (field) => (e) => {
    const value = e.target.value;
    // Allow only digits (or empty string)
    if (/^\d*$/.test(value)) {
      setForm({ ...form, [field]: value });
    }
  };

  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 12) digits = digits.slice(0, 12);
    setForm({ ...form, phoneNumber: digits });
    if (errors.phoneNumber) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phoneNumber;
        return next;
      });
    }
  };

  const handlePhoneBlur = () => {
    const normalized = normalizeSenegalPhoneInput(form.phoneNumber);
    if (normalized !== form.phoneNumber) {
      setForm((prev) => ({ ...prev, phoneNumber: normalized }));
    }
  };

  /* update user api call */
  async function updateUserDetails(form, successMessage) {
    const userObj = {
      user_id: editingUserId,
      first_name: form.first_name,
      last_name: form.last_name,
      phone_number: normalizeSenegalPhoneInput(form.phoneNumber),
      email_id: form.email_id,
      state: form.state,
      district: form.district,
      language: form.language,
    };

    const defaultError = t(
      "admin.annotators.modals.errors.updateAnnotator",
      "Failed to update annotator"
    );

    try {
      const result = await updateUser(userObj);
      if (result.status === "success") {
        showSnackbar("success", successMessage);
        onClose();
        return true;
      }

      const msg = translateApiMessage(result.message, result.code, t, defaultError);
      if (isEmailAlreadyExistsError(result)) {
        setErrors((prev) => ({ ...prev, email_id: msg }));
      } else if (isPhoneInvalidFormatError(result)) {
        setErrors((prev) => ({ ...prev, phoneNumber: msg }));
      }
      showSnackbar("error", msg);
      return false;
    } catch (error) {
      console.error("Failed to update user:", error);
      showSnackbar("error", error?.message || defaultError);
      return false;
    }
  }

  const renderSelectField = (
    label,
    field,
    field_value,
    options,
    handler,
    option_value,
    option_label
  ) => (
    <FormControl fullWidth error={Boolean(errors[field])}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        value={field_value}
        label={label}
        onChange={handler ? handler(field) : handleChange(field)}
        sx={{
          height: 50,
          fontSize: "1rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
            color: "gray",
          },
        }}
        onOpen={() => setOpenDropdown((prev) => ({ ...prev, [field]: true }))}
        onClose={() => setOpenDropdown((prev) => ({ ...prev, [field]: false }))}
        IconComponent={() => (
          <FontAwesomeIcon icon={openDropDown[field] ? faChevronUp : faChevronDown} />
        )}
      >
        {option_value !== "" && option_label !== ""
          ? options.map((option) => (
              <MenuItem key={option[option_value]} value={option[option_value]}>
                {option[option_label]}
              </MenuItem>
            ))
          : options.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
      </Select>
      {errors[field] && (
        <p style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>{errors[field]}</p>
      )}
    </FormControl>
  );

  const renderTextField = (label, field, field_value, type = "text") => (
    <TextField
      label={label}
      type={type}
      fullWidth
      value={field_value}
      onChange={handleChange(field)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      variant="outlined"
      sx={{ fontSize: "1rem" }}
    />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">
              {title ||
                (t
                  ? t("admin.annotators.modals.editAnnotator", "Edit Annotator")
                  : "Edit Annotator")}
            </MDTypography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.firstName", "First Name") : "First Name",
                "first_name",
                form.first_name
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.lastName", "Last Name") : "Last Name",
                "last_name",
                form.last_name
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={
                  t
                    ? t("admin.annotators.modals.fields.phoneNumber", "Phone Number")
                    : "Phone Number"
                }
                fullWidth
                type="text"
                value={form.phoneNumber}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
                variant="outlined"
                placeholder="77XXXXXXX"
                inputProps={{ maxLength: 12, inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: "0.875rem", color: "#666" }}>+221</span>
                    </InputAdornment>
                  ),
                }}
                sx={{ fontSize: "1rem" }}
              />
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.email", "Email") : "Email",
                "email_id",
                form.email_id
              )}
            </Grid>
            <Grid item xs={12}>
              <HierarchySelects
                regions={hierarchy.regions}
                departments={hierarchy.departments}
                ias={hierarchy.ias}
                iefs={hierarchy.iefs}
                selectedRegionId={hierarchy.selectedRegionId}
                selectedDepartmentId={hierarchy.selectedDepartmentId}
                selectedIaId={hierarchy.selectedIaId}
                selectedIefId={hierarchy.selectedIefId}
                onRegionChange={hierarchy.handleRegionChange}
                onDepartmentChange={hierarchy.handleDepartmentChange}
                onIaChange={hierarchy.handleIaChange}
                onIefChange={handleHierarchyIefChange}
                disabled={hierarchy.loading}
                districtError={errors.district}
              />
            </Grid>
            <Grid item xs={6}>
              {renderSelectField(
                t ? t("admin.annotators.modals.fields.language", "Language") : "Language",
                "language",
                form.language,
                languages,
                handleChange,
                "language",
                "language"
              )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleSubmit}
                disabled={isSaving}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "2px solid #218838",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#218838",
                    borderColor: "#1e7e34",
                  },
                  "&:active": {
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                  },
                }}
              >
                {isSaving
                  ? t("admin.annotators.modals.buttons.saving", "Saving...")
                  : t
                  ? t("admin.annotators.modals.buttons.save", "Save")
                  : "Save"}
              </Button>
              <Button
                onClick={handleCancel}
                disabled={isSaving}
                variant="contained"
                sx={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "2px solid #5a6268",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: "#5a6268",
                    borderColor: "#545b62",
                  },
                }}
              >
                {t ? t("admin.annotators.modals.buttons.cancel", "Cancel") : "Cancel"}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

EditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
  successMessageKey: PropTypes.string,
};

EditUser.defaultProps = {
  title: null,
  user: null,
  successMessageKey: null,
};
